// Copyright (c) 2026 FlexiProof. All rights reserved.
//
// This source code file is licensed under the terms of the MIT license, a copy
// of which may be found in the LICENSE.md file in the root of this repository.
//
// For a template copy of the license see one of the following 3rd party sites:
//      - <https://opensource.org/licenses/MIT>
//      - <https://choosealicense.com/licenses/mit>
//      - <https://spdx.org/licenses/MIT>

/**
 * @file Deck selector step renderer.
 * @author James Reid
 */

// @ts-check

// @@imports-dependencies
import { Deck } from "../../../../package/Deck.js"
import { presets } from "../../../../package/presets/deck.js"
import { App } from "../../app/index.js"

// @@imports-package
import { SteppedContainer } from "../../components/index.js"

// @@imports-types
import { State } from "../../types/index.js"
import { cloneTemplate, fadeElementIn, fadeElementOut, getDeckImageUrl } from "../../utils/index.js"
import { Layout } from "../Layout.js"

// @@body
const deckSelector = new Layout(async () => {
    // 
    const { container, state } = App.instance
    const content = SteppedContainer.contentContainer()

    const deckOptions = [
        presets.rectangular,
        presets.rightL,
        presets.standardT,
        presets.standardS,
        presets.standardU
    ]
    deckOptions.sort((deckOptionA, deckOptionB) => {
        return deckOptionA.deck.edges.size - deckOptionB.deck.edges.size
    })

    for (const deckOption of deckOptions) {
        deckOption.imageUrl = await getDeckImageUrl(deckOption)
    }

    if (state.deckOption) {
        Object.assign(state.deckOption, {
            imageUrl: null,
            id: "client-custom",
            title: "Your Deck",
            description: "Continue where you left off."
        })
        deckOptions.unshift(state.deckOption)
    }

    const standardRoofs = document.createElement("div")
    const standardHeading = document.createElement("h3")
    standardHeading.innerText = "Standard Roofs"
    standardRoofs.append(standardHeading)

    const complexRoofs = document.createElement("div")
    const complexHeading = document.createElement("h3")
    complexHeading.innerText = "Complex Roofs"
    complexRoofs.append(complexHeading)

    for (const deckOption of deckOptions) {
        const card = cloneTemplate("deck-option")
        card.dataset.deckId = deckOption.id

        const details = document.createElement("div")
        details.classList.add("details")

        const title = /** @type {HTMLElement?} */ (card.querySelector(".title"))
        if (title) { title.innerText = deckOption.title }

        const description = /** @type {HTMLElement?} */
            (card.querySelector("p"))
        if (description) { description.innerText = deckOption.description }

        const edges = /** @type {HTMLElement?} */
            (card.querySelector("p.small.wide"))
        if (edges) { edges.innerText = `${deckOption.deck.edges.size} edges` }

        const image = document.createElement("img")
        image.src = deckOption.imageUrl || await getDeckImageUrl(deckOption)

        card.addEventListener("click", () => {
            state.deck.client = new Deck({
                vertices: deckOption.deck.copy().vertices.array,
                isClosed: true
            })
            state.deck.id = deckOption.id
            // state.deckOption = {
            //     ...deckOption,
            //     deck: new Deck({
            //         vertices: deckOption.deck.copy().vertices.array,
            //         isClosed: true
            //     })
            // }
            // state.resetDeckOption = {
            //     ...deckOption,
            //     deck: new Deck({
            //         vertices: deckOption.deck.copy().vertices.array,
            //         isClosed: true
            //     })
            // }
        })

        if (deckOption.deck === state.deckOption?.deck) {
            card.classList.add("selected")
        }

        card.querySelector(".horizontal-container")?.append(image)

        deckOption.deck === state.deckOption?.deck ? content.append(card)
            : deckOption.deck.edges.size < 8 ? standardRoofs.append(card)
            : complexRoofs.append(card)
    }

    if (!state.deckOption) {
        const deckOption = deckOptions[0]
        state.deckOption = {
            ...deckOption,
            deck: new Deck({
                vertices: deckOption.deck.copy().vertices.array,
                isClosed: true
            })
        }
        state.resetDeckOption = {
            ...deckOption,
            deck: new Deck({
                vertices: deckOption.deck.copy().vertices.array,
                isClosed: true
            })
        }

        standardRoofs.querySelector("div.card").classList.add("selected")
    }

    standardRoofs.classList.add("vertical-container", "gap-standard")
    complexRoofs.classList.add("vertical-container", "gap-standard")

    content.append(standardRoofs, complexRoofs)

    content.addEventListener("click", (event) => {
        const cards = /** @type {HTMLDivElement[]} */ (content
            .querySelectorAll("div.card.selectable"))
        for (let i = 0; i < cards.length; i++) {
            const card = cards[i]
            if (card.classList.contains("info")) { continue }
            if (card.dataset.deckId === state.deck.id) {
                card.classList.add("selected")
            }
            else { card.classList.remove("selected") }
        }
    })

    document.querySelector("nav button.primary")?.classList.remove("inactive")

    container.load(content)
}, {
    id: "deck-selector",
    title: "Set Deck Shape",
    name: "Shape",
})

// @@exports
export { deckSelector }
